<template>
  <div>
    <div class="stock_item">
      <div class="stock_item_left">
        <div class="product_name">
          {{ localProduct.code ? `${localProduct.name} (${localProduct.code})` : localProduct.name }}
        </div>
        <div class="product_type product-type-row">
          <div class="product-type-name">
            {{ localProduct.product_type ? localProduct.product_type.name : "Altele" }}
          </div>
          <button type="button" class="product-remove-button" :title="$t('common.remove')" @click="emitRemove">
            <i class="fa fa-trash product-remove-icon" aria-hidden="true"></i>
          </button>
        </div>
        <div class="product_type">
          <div v-if="!showWarehouseSelector">
            Stoc:
            {{ (localProduct.warehouse_quantity ?? 0) | getFormattedQuantity }}
            {{ localProduct?.measure_unit?.symbol }}
          </div>
        </div>
        <div class="qty_dose_row">
          <div class="stock_quantity">
            <div class="product-editor-label">{{ $t("common.quantity") }}</div>
            <DxNumberBox
              v-model="localProduct.quantity"
              :min="0"
              value-change-event="keyup"
              class="global_dx_number_input"
              :show-clear-button="false"
              :format="quantityFormatter + ' ' + (measureUnitSymbol || '')"
              @value-changed="onQuantityChanged($event)"
            />
          </div>
          <div v-if="showUnitPrice" class="stock_quantity">
            <div class="product-editor-label">{{ $t("stock_mobile.form.unit_price") }}</div>
            <DxNumberBox
              v-model="localProduct.unit_price"
              :min="0"
              value-change-event="keyup"
              class="global_dx_number_input"
              :show-clear-button="false"
              format="#,##0.00 RON"
              @value-changed="onUnitPriceChanged($event)"
            />
          </div>
          <div v-if="showDose" class="stock_quantity">
            <div class="product-editor-label">{{ $t("common.dose") }}</div>
            <DxNumberBox
              v-model="localProduct.dose"
              :min="0"
              value-change-event="keyup"
              class="global_dx_number_input"
              :show-clear-button="false"
              :format="quantityFormatter + ' ' + (measureUnitSymbol || '') + '/ha'"
              @value-changed="onDoseChanged($event)"
            />
            <div class="dose_area" v-if="totalArea > 0">{{ formatQty(totalArea) }} ha</div>
          </div>
        </div>
        <div v-if="showWarehouseSelector" class="product-editor-field warehouse_selector">
          <div class="product-editor-label">{{ $t("common.warehouse") }}</div>
          <DxSelectBox
            v-model="localProduct.warehouse_uuid"
            :search-enabled="true"
            search-mode="contains"
            search-expr="name"
            class="dx_input_value"
            :placeholder="$t('common.select_warehouse')"
            :data-source="resolvedWarehouseOptions"
            :display-expr="getWarehouseDisplayName"
            value-expr="uuid"
            :show-clear-button="false"
            @selection-changed="onWarehouseChanged"
          >
            <template #item="{ data }">
              <div class="dx_custom_combo_item">{{ data?.name || "-" }} ({{ formatQty(data?.quantity) }} {{ measureUnitSymbol }})</div>
            </template>
          </DxSelectBox>
        </div>
      </div>
      <div class="expand_icon_holder" :class="{ expand_icon_placeholder: !isLotManaged }">
        <div :class="lotsExpanded ? 'expand_icon_expanded' : 'expand_icon'" @click="toggleLots">
          <img v-show="lotsExpanded" src="@/assets/images/icons/arrow_up_green.svg" />
          <img v-show="!lotsExpanded" src="@/assets/images/icons/arrow_down.svg" />
        </div>
      </div>
    </div>
    <div v-if="lotErrorMessage" class="lot-error">{{ lotErrorMessage }}</div>
    <div v-if="isLotManaged && lotsExpanded" class="lot-grid-wrapper">
      <div class="lot-summary">
        <div class="lot-summary-title">{{ $t("lot_items.title") }}</div>
        <div class="lot-summary-values">{{ selectedTotal }} {{ measureUnitSymbol }} / {{ totalAvailable }} {{ measureUnitSymbol }}</div>
      </div>
      <div v-if="loading" class="lot-loading">{{ $t("lot_items.loading") }}</div>
      <div v-else-if="!items.length" class="lot-empty">
        {{ $t("lot_items.available_empty") }}
      </div>
      <DxDataGrid
        v-else
        ref="lotGrid"
        class="grid_general_mobile_style product-lot-grid"
        :data-source="items"
        key-expr="group_key"
        :remote-operations="false"
        :hover-state-enabled="true"
        :repaint-changes-only="true"
        :selected-row-keys="selectedRowKeys"
        :element-attr="gridAttributes"
        :no-data-text="$t('lot_items.available_empty')"
        height="240"
        @row-click="onRowClick"
        @row-updated="onRowUpdated"
        @cell-prepared="onCellPrepared"
      >
        <DxSelection mode="multiple" showCheckBoxesMode="none" :select-by-click="true" />
        <DxFilterRow :visible="true" />
        <DxPaging :enabled="true" :page-size="10" />
        <DxScrolling mode="virtual" />
        <DxSorting mode="multiple" />
        <DxEditing mode="cell" :allow-updating="true" />

        <DxColumn
          data-field="lot_search"
          :caption="$t('lot_items.lot')"
          cell-template="lotCell"
          :allow-sorting="false"
          :allow-editing="false"
        />
        <DxColumn
          data-field="expiration_date_display"
          :caption="$t('common.expiration_date')"
          cell-template="expirationCell"
          :calculate-sort-value="getExpirationSortValue"
          sort-order="asc"
          :sort-index="0"
          :allow-editing="false"
          width="120px"
        />
        <DxColumn
          data-field="_selected_quantity"
          :caption="$t('common.quantity') + ' (' + (measureUnitSymbol || '') + ')'"
          alignment="right"
          :dataType="'number'"
          :allow-filtering="false"
          :allow-sorting="false"
        />

        <template #lotCell="{ data }">
          <div class="lot-cell">
            <div class="lot-cell-main">{{ data.data.lot || "-" }}</div>
            <div class="lot-cell-sub">{{ formatQty(data.data.available_quantity) }} {{ measureUnitSymbol }}</div>
          </div>
        </template>
        <template #expirationCell="{ data }">
          <span>{{ data.data.expiration_date_display || "-" }}</span>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxNumberBox from "devextreme-vue/number-box";
import DxSelectBox from "devextreme-vue/select-box";
import { DxDataGrid, DxColumn, DxSelection, DxFilterRow, DxPaging, DxScrolling, DxSorting, DxEditing } from "devextreme-vue/data-grid";
import moment from "moment";
import { getQuantityFormatter, getFormattedQuantity, mapAvailableLotGroups, splitQuantityAcrossLotRows, aggregateLotItemsByGroup } from "~/helpers/stockHelper";

export default {
  name: "ProductSingleEditor",
  components: {
    DxNumberBox,
    DxSelectBox,
    DxDataGrid,
    DxColumn,
    DxSelection,
    DxFilterRow,
    DxPaging,
    DxScrolling,
    DxSorting,
    DxEditing,
  },
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    totalArea: {
      type: Number,
      default: 0,
    },
    warehouseOptions: {
      type: Array,
      default: () => [],
    },
    movementDate: {
      type: String,
      default: "",
    },
    showWarehouseSelector: {
      type: Boolean,
      default: true,
    },
    showUnitPrice: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localProduct: this.normalizeProduct(this.value),
      lotsExpanded: false,
      loading: false,
      showLotError: false,
      items: [],
      selectedRowKeys: [],
      lastEmittedSnapshot: "",
      lotsLoaded: false,
      lotsLoadedKey: "",
      calcLock: false,
      gridAttributes: {
        class: "smagria-grid product-lot-grid",
      },
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    displayName() {
      const name = this.localProduct?.name || "-";
      const code = this.localProduct?.code ? ` (${this.localProduct.code})` : "";
      return `${name}${code}`;
    },
    quantityFormatter() {
      return getQuantityFormatter();
    },
    showDose() {
      return this.totalArea > 0;
    },
    isLotManaged() {
      return !!this.localProduct?.lot_management;
    },
    measureUnitSymbol() {
      return this.localProduct?.measure_unit?.symbol || "";
    },
    resolvedWarehouseOptions() {
      if (Array.isArray(this.warehouseOptions) && this.warehouseOptions.length > 0) {
        return this.warehouseOptions;
      }
      const stockList = Array.isArray(this.localProduct?.stock_list) ? this.localProduct.stock_list : [];
      return stockList.map((item) => ({
        uuid: item.warehouse_uuid || item.uuid,
        name: item.warehouse_name || item.name || "",
        quantity: item.quantity,
        legal_entity_uuid: item.legal_entity_uuid,
      }));
    },
    selectedTotal() {
      const total = this.items.reduce((sum, item) => {
        return sum + (parseFloat(item._selected_quantity) || 0);
      }, 0);
      return parseFloat(total.toFixed(4));
    },
    totalAvailable() {
      const total = this.items.reduce((sum, item) => {
        return sum + (parseFloat(item.available_quantity) || 0);
      }, 0);
      return parseFloat(total.toFixed(4));
    },
    isSumValid() {
      const qty = parseFloat(this.localProduct?.quantity) || 0;
      if (qty <= 0) {
        return false;
      }
      const sum = this.items.length ? this.selectedTotal : this.getLotItemsSum();
      return Math.abs(sum - qty) <= 0.0001;
    },
    lotErrorMessage() {
      if (!this.showLotError) {
        return "";
      }
      return this.getLotError();
    },
  },
  watch: {
    value: {
      handler(newVal) {
        const snapshot = this.safeSnapshot(newVal);
        if (snapshot && snapshot === this.lastEmittedSnapshot) {
          return;
        }
        this.localProduct = this.normalizeProduct(newVal);
        if (this.lotsExpanded) {
          this.fetchAvailable();
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    normalizeProduct(product) {
      const base = product || {};
      return {
        ...base,
        dose: base.dose ?? 0,
        lot_items: Array.isArray(base.lot_items) ? base.lot_items : [],
      };
    },
    emitChange() {
      this.lastEmittedSnapshot = this.safeSnapshot(this.localProduct);
      this.$emit("input", { ...this.localProduct });
    },
    emitRemove() {
      this.$emit("remove", this.localProduct?.uuid || null);
    },
    getLotError() {
      if (!this.isQtyWithinWarehouse()) {
        return this.$t("lot_items.sum_mismatch");
      }
      if (this.items.length) {
        if (this.items.some((item) => item._selected_quantity && !this.isQtyValid(item))) {
          return this.$t("lot_items.invalid_quantity");
        }
        if (!this.isSumValid) {
          return this.$t("lot_items.sum_mismatch");
        }
      } else {
        if (!this.areLotItemsValid()) {
          return this.$t("lot_items.invalid_quantity");
        }
        if (!this.isSumValid) {
          return this.$t("lot_items.sum_mismatch");
        }
      }
      return "";
    },
    isValid() {
      if (this.showUnitPrice && !Number.isFinite(parseFloat(this.localProduct?.unit_price))) {
        return false;
      }
      if (!this.isQtyWithinWarehouse()) {
        return false;
      }
      if (!this.isLotManaged && !!this.localProduct.quantity && this.localProduct.quantity > 0) {
        return true;
      }
      if (!this.items.length && this.isLotManaged) {
        return this.isSumValid && this.areLotItemsValid() && !!this.localProduct.quantity && this.localProduct.quantity > 0;
      }
      return !this.getLotError() && !!this.localProduct.quantity && this.localProduct.quantity > 0;
    },
    safeSnapshot(value) {
      try {
        return JSON.stringify(value);
      } catch (e) {
        return "";
      }
    },
    onQuantityChanged(e) {
      // Only schedule for user input (DevExtreme sends e.event for user-triggered changes)
      if (!e || !e.event) return;

      this.localProduct.quantity = this.toNumber(this.localProduct.quantity, 0);
      this.updateDoseFromQuantity();
      this.emitChange();
    },
    onUnitPriceChanged(e) {
      if (!e || !e.event) return;

      this.localProduct.unit_price = this.toNumber(this.localProduct.unit_price, 0);
      this.emitChange();
    },
    onDoseChanged(e) {
      // Only schedule for user input; programmatic updates (from qty) won’t re-trigger
      if (!e || !e.event) return;

      this.localProduct.dose = this.toNumber(this.localProduct.dose, 0);
      this.updateQuantityFromDose();
      this.emitChange();
    },
    onWarehouseChanged(e) {
      if (e && e.selectedItem) {
        this.localProduct.warehouse_uuid = e.selectedItem.uuid;
        this.localProduct.warehouse_name = e.selectedItem.name || "";
        this.localProduct.warehouse_quantity = e.selectedItem.quantity ?? null;
        this.localProduct.warehouse_department_legal_entity_uuid = e.selectedItem.legal_entity_uuid ?? null;
        this.emitChange();
        if (this.lotsExpanded) {
          this.fetchAvailable();
        }
      }
    },
    updateDoseFromQuantity() {
      if (this.calcLock || !this.showDose) {
        return;
      }
      const qty = this.toNumber(this.localProduct.quantity, 0);
      const area = this.toNumber(this.totalArea, 0);
      const dose = qty > 0 && area > 0 ? this.round(qty / area, 4) : 0;

      this.calcLock = true;
      this.localProduct.dose = dose;
      this.calcLock = false;
    },
    updateQuantityFromDose() {
      if (this.calcLock || !this.showDose) {
        return;
      }
      const dose = this.toNumber(this.localProduct.dose, 0);
      const area = this.toNumber(this.totalArea, 0);
      const qty = dose > 0 && area > 0 ? this.round(area * dose, 4) : 0;
      this.calcLock = true;
      this.localProduct.quantity = qty;
      this.calcLock = false;
    },
    getWarehouseDisplayName(item) {
      if (!item) {
        return this.$t("common.select_warehouse");
      }
      const qty = getFormattedQuantity(item.quantity ?? 0);
      return `${item.name || "-"} (${qty} ${this.measureUnitSymbol})`;
    },
    toggleLots() {
      this.lotsExpanded = !this.lotsExpanded;
      if (this.lotsExpanded) {
        if (!this.lotsLoaded || this.lotsLoadedKey !== this.getLotsKey()) {
          this.fetchAvailable();
        }
      }
    },
    async fetchAvailable() {
      if (!this.localProduct?.uuid || !this.localProduct?.warehouse_uuid || !this.movementDate) {
        this.items = [];
        this.selectedRowKeys = [];
        this.lotsLoaded = false;
        this.lotsLoadedKey = "";
        return;
      }
      const lotsKey = this.getLotsKey();
      this.loading = true;
      try {
        const payload = {
          product_uuid: this.localProduct.uuid,
          warehouse_uuid: this.localProduct.warehouse_uuid,
          movement_date: this.movementDate,
          enablePagination: 0,
          limit: 99999,
        };
        const response = await this.getSmStockMovementItemsAvailableSum(payload);
        const data = response?.data?.data || response?.data || [];
        const groups = mapAvailableLotGroups(data);
        // Aggregate previously saved row-level lot_items back to group quantities
        const groupTotals = aggregateLotItemsByGroup(groups, this.localProduct.lot_items || []);
        this.items = groups.map((group) => {
          const selectedQty = groupTotals.get(group.group_key) || 0;
          return {
            ...group,
            _selected: selectedQty > 0,
            _selected_quantity: selectedQty > 0 ? selectedQty : 0,
            expiration_date_raw: group.expiration_date || null,
            expiration_date_display: this.formatExpirationDate(group.expiration_date),
            lot_search: `${group.lot || ""} ${this.formatQty(group.available_quantity)} ${this.measureUnitSymbol || ""}`.trim(),
          };
        });
        this.selectedRowKeys = this.items.filter((item) => (parseFloat(item._selected_quantity) || 0) > 0).map((item) => item.group_key);
        this.lotsLoaded = true;
        this.lotsLoadedKey = lotsKey;
      } finally {
        this.loading = false;
      }
    },
    getLotsKey() {
      const productUuid = this.localProduct?.uuid || "";
      const warehouseUuid = this.localProduct?.warehouse_uuid || "";
      const movementDate = this.movementDate || "";
      return `${productUuid}|${warehouseUuid}|${movementDate}`;
    },
    onRowClick(e) {
      if (e.rowType !== "data") {
        return;
      }
      const rowKey = e.key;
      const rowIndex = this.items.findIndex((item) => item.group_key === rowKey);
      if (rowIndex === -1) {
        return;
      }

      const row = this.items[rowIndex];
      const currentQty = parseFloat(row._selected_quantity) || 0;
      const nextQty = currentQty; // > 0 ? 0 : parseFloat(row.available_quantity) || 0;
      this.$set(this.items, rowIndex, {
        ...row,
        _selected: nextQty > 0,
        _selected_quantity: nextQty,
      });

      this.selectedRowKeys = this.items.filter((item) => (parseFloat(item._selected_quantity) || 0) > 0).map((item) => item.group_key);
      this.$refs.lotGrid.instance.selectRows(this.selectedRowKeys, false);
      this.syncLotItemsFromSelection();
      this.showLotError = false;
    },
    onRowUpdated(e) {
      const rowKey = e.key;
      const rowIndex = this.items.findIndex((item) => item.group_key === rowKey);
      if (rowIndex === -1) {
        return;
      }
      const row = this.items[rowIndex];
      const max = parseFloat(row.available_quantity) || 0;
      let qty = parseFloat(row._selected_quantity) || 0;

      if (qty > max) {
        qty = max;
      }
      if (qty < 0) {
        qty = 0;
      }

      const isSelected = qty > 0;
      this.$set(this.items, rowIndex, {
        ...row,
        _selected: isSelected,
        _selected_quantity: qty,
      });
      this.selectedRowKeys = this.items.filter((item) => (parseFloat(item._selected_quantity) || 0) > 0).map((item) => item.group_key);
      this.$refs.lotGrid.instance.selectRows(this.selectedRowKeys, false);
      this.syncLotItemsFromSelection();
      this.showLotError = false;
    },
    onCellPrepared(e) {
      if (e?.rowType !== "data") {
        return;
      }
      if (e?.column?.dataField === "_selected_quantity") {
        e.cellElement.classList.add("lot-editable-cell");
      }
    },
    syncLotItemsFromSelection() {
      // Split each selected lot group quantity across its underlying stock
      // movement item rows (FIFO), so the payload stays row-level as before.
      const selected = this.items
        .filter((item) => (parseFloat(item._selected_quantity) || 0) > 0)
        .flatMap((item) => splitQuantityAcrossLotRows(item, parseFloat(item._selected_quantity) || 0));
      this.localProduct.lot_items = selected;
      this.emitChange();
    },
    getLotItemsSum() {
      const items = Array.isArray(this.localProduct?.lot_items) ? this.localProduct.lot_items : [];
      return items.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0);
    },
    areLotItemsValid() {
      const items = Array.isArray(this.localProduct?.lot_items) ? this.localProduct.lot_items : [];
      if (!items.length) {
        return false;
      }
      return items.every((item) => (parseFloat(item.quantity) || 0) > 0);
    },
    formatQty(value) {
      const num = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(num) ? parseFloat(num.toFixed(4)) : 0;
    },
    formatExpirationDate(value) {
      if (!value) {
        return "-";
      }
      const date = moment(value);
      if (!date.isValid()) {
        return value;
      }
      return date.format(this.getDefaultDateFormat);
    },
    getExpirationSortValue(rowData) {
      return rowData?.expiration_date_raw || rowData?.expiration_date || null;
    },
    isQtyValid(item) {
      const qty = parseFloat(item._selected_quantity) || 0;
      const max = parseFloat(item.available_quantity) || 0;
      return qty > 0 && qty <= max;
    },
    isQtyWithinWarehouse() {
      const qty = parseFloat(this.localProduct?.quantity) || 0;
      const max = parseFloat(this.localProduct?.warehouse_quantity);
      if (!Number.isFinite(max)) {
        return true;
      }
      return qty <= max;
    },
    toNumber(value, def) {
      const num = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(num) ? num : def;
    },
    round(value, decimals) {
      const factor = Math.pow(10, decimals);
      return Math.round(this.toNumber(value, 0) * factor) / factor;
    },
    validate() {
      if (this.showUnitPrice && !Number.isFinite(parseFloat(this.localProduct?.unit_price))) {
        return false;
      }
      if (this.isLotManaged) {
        this.showLotError = true;
        return !this.getLotError();
      }
      return true;
    },
  },
};
</script>

<style scoped>
.fa-trash {
  color: red;
}

.selected_product_action_icon {
  font-size: 18px;
  padding: 10px;
}

.product-editor {
  background: #ffffff;
  border: 1px solid #e4e4e4;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}

.product-editor-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.product-editor-title {
  font-weight: 700;
  font-size: 14px;
}

.product-editor-actions {
  display: flex;
  align-items: center;
}

.lot-toggle {
  border: none;
  background: transparent;
  padding: 4px;
}

.product-editor-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

.product-editor-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-meta-row {
  flex-direction: row;
  align-items: start;
  justify-content: start;
}

.product-meta-quantity {
  white-space: nowrap;
}

.product-type-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.product-type-name {
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-remove-button {
  border: none;
  background: transparent;
  padding: 4px;
  margin-left: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.product-remove-icon {
  font-size: 16px;
  line-height: 1;
}

.product-editor-label {
  font-size: 12px;
  font-weight: 600;
  color: #4a4a4a;
}

.lot-grid-wrapper {
  margin-top: 10px;
  padding: 10px;
}

.lot-summary {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
}

.lot-loading,
.lot-empty {
  font-size: 12px;
  color: #797979;
  margin-bottom: 8px;
}

.lot-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.lot-cell-main {
  font-weight: 600;
  font-size: 13px;
}

.lot-cell-sub {
  font-size: 12px;
  color: #6b6b6b;
}

.lot-error {
  margin-top: 6px;
  color: #dc3545;
  font-size: 12px;
}

.product-editor-footer {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
}

.stock_item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.stock_item_left {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.qty_dose_row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.stock_quantity {
  flex: 1;
  min-width: 0;
}

.warehouse_selector {
  margin-top: 4px;
}

.stock_quantity .global_dx_number_input {
  border: 1px solid #e4e4e4;
  background-color: #fefbdf;
  border-radius: 6px;
  padding: 2px 2px;
  margin-left: 2px;
}

.warehouse_selector .dx-texteditor {
  border: 1px solid #e4e4e4;
  background-color: #fefbdf;
  border-radius: 6px;
}

.dose_area {
  font-size: 12px;
  color: #6b6b6b;
  margin-top: 4px;
}

.expand_icon_holder {
  width: 44px;
  min-width: 44px;
  flex: 0 0 44px;
  margin-left: 4px;
  margin-right: 12px;
  align-self: center;
}

.expand_icon_placeholder {
  visibility: hidden;
}
</style>

<style>
.lot-editable-cell {
  background-color: #fefbdf !important; /* light yellow */
}
</style>
